import React, { useState } from 'react';
import { Briefcase, Users, Phone, Mail, Menu, X, ArrowRight, Clock, BookOpen, Coffee, Globe, BarChart, Server, Code } from 'lucide-react';

// Main App Component
const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const navLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Services', href: '#services' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'About Us', href: '#about' },
    { name: 'Careers', href: '#careers' },
    { name: 'Contact', href: '#contact' },
  ];

  // Function to handle smooth scrolling
  const handleNavClick = (e) => {
    e.preventDefault();
    const href = e.currentTarget.href;
    const targetId = href.replace(/.*#/, "");
    const elem = document.getElementById(targetId);
    
    if (elem) {
        elem.scrollIntoView({
            behavior: "smooth",
            block: "start",
        });
    }
    
    // Close mobile menu if it's open
    if (isMenuOpen) {
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="bg-black text-white font-sans">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black bg-opacity-80 backdrop-blur-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex-shrink-0">
              <a href="#home" onClick={handleNavClick} className="flex items-center">
                <span className="text-2xl font-bold">RefacTech</span>
              </a>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.href}
                    onClick={handleNavClick}
                    className="text-gray-300 hover:bg-gray-800 hover:text-white px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300"
                  >
                    {link.name}
                  </a>
                ))}
              </div>
            </div>
            <div className="-mr-2 flex md:hidden">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                type="button"
                className="bg-gray-900 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                aria-controls="mobile-menu"
                aria-expanded="false"
              >
                <span className="sr-only">Open main menu</span>
                {isMenuOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {isMenuOpen && (
          <div className="md:hidden" id="mobile-menu">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={handleNavClick}
                  className="text-gray-300 hover:bg-gray-700 hover:text-white block px-3 py-2 rounded-md text-base font-medium transition-colors duration-300"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section id="home" className="relative h-[calc(100vh-80px)] min-h-[500px] flex items-center justify-center text-center overflow-hidden">
             <div className="absolute inset-0 bg-black opacity-50"></div>
             <div className="absolute inset-0 z-0">
                <div className="absolute bg-green-900/30 rounded-full w-96 h-96 -top-20 -left-20 filter blur-3xl opacity-50 animate-pulse"></div>
                <div className="absolute bg-green-700/30 rounded-full w-96 h-96 -bottom-20 -right-20 filter blur-3xl opacity-50 animate-pulse animation-delay-4000"></div>
            </div>
            <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4">
                    <span className="text-green-400">RefacTech:</span> Engineering Your Software's Future
                </h1>
                <p className="max-w-3xl mx-auto text-lg md:text-xl text-gray-300 mb-8">
                    From custom development and legacy modernization to managed operations and performance scaling, we are your dedicated partner in technological excellence.
                </p>
                <a href="#services" onClick={handleNavClick} className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-black bg-green-400 hover:bg-green-500 md:text-lg transition-transform transform hover:scale-105 shadow-lg">
                    Explore Our Services <ArrowRight className="ml-2 h-5 w-5" />
                </a>
            </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 md:py-32 bg-green-900/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-extrabold">Our Expertise</h2>
              <p className="mt-4 text-lg text-gray-400">Driving innovation by transforming your technological core.</p>
               <div className="mt-2 h-1 w-20 bg-green-400 mx-auto rounded"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8 max-w-4xl mx-auto">
              <div className="bg-gray-900 p-8 rounded-lg shadow-lg hover:shadow-green-400/20 transition-shadow duration-300 transform hover:-translate-y-2">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-green-400 text-black mb-6">
                  <Briefcase className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-4">Custom Software Solutions</h3>
                <p className="text-gray-400">
                  We partner with businesses to design, build, and deploy tailor-made software solutions that address unique challenges and drive growth.
                </p>
              </div>
              <div className="bg-gray-900 p-8 rounded-lg shadow-lg hover:shadow-green-400/20 transition-shadow duration-300 transform hover:-translate-y-2">
                <div className="flex items-center justify-center h-16 w-16 rounded-full bg-green-400 text-black mb-6">
                   <Server className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-4">Managed Product Operations</h3>
                <p className="text-gray-400">
                  Let us handle the technical operations of your business products, ensuring they run smoothly and reliably so you can focus on your core business.
                </p>
              </div>
              <div className="bg-gray-900 p-8 rounded-lg shadow-lg hover:shadow-green-400/20 transition-shadow duration-300 transform hover:-translate-y-2">
                 <div className="flex items-center justify-center h-16 w-16 rounded-full bg-green-400 text-black mb-6">
                  <BarChart className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-4">Scalability & Performance</h3>
                <p className="text-gray-400">
                  We engineer your services to handle growing customer demand, optimizing your infrastructure for high performance, reliability, and cost-effectiveness.
                </p>
              </div>
               <div className="bg-gray-900 p-8 rounded-lg shadow-lg hover:shadow-green-400/20 transition-shadow duration-300 transform hover:-translate-y-2">
                 <div className="flex items-center justify-center h-16 w-16 rounded-full bg-green-400 text-black mb-6">
                  <Code className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold mb-4">Refactoring Legacy Code</h3>
                <p className="text-gray-400">
                  We breathe new life into your old codebases, improving maintainability, performance, and security without disrupting your core business operations.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-20 md:py-32">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-3xl md:text-4xl font-extrabold">What Our Clients Say</h2>
                    <div className="mt-2 h-1 w-20 bg-green-400 mx-auto rounded"></div>
                </div>
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-1 lg:grid-cols-3 gap-8">
                    <div className="bg-gray-800 p-8 rounded-lg shadow-lg flex flex-col">
                        <blockquote className="text-lg text-gray-300 italic mb-6 flex-grow">
                            "Divine Covenant Education is an outfit for special needs children and much like the needs of our clients' children, we needed a custom tailored solution to meet our goals. RefacTech was able to deliver just the right solution for us."
                        </blockquote>
                        <div>
                            <p className="font-bold text-white">Divine Arowolo</p>
                            <p className="text-sm text-gray-400">CEO, Divine Covenant Education</p>
                        </div>
                    </div>
                     <div className="bg-gray-800 p-8 rounded-lg shadow-lg flex flex-col">
                        <blockquote className="text-lg text-gray-300 italic mb-6 flex-grow">
                            "Thanks to RefacTech's HumaneResource, our HR operations at Ken & Kay Accounting have been completely streamlined. The efficiency gains are remarkable, allowing our team to shift their focus from administrative tasks back to core client services. It's a game-changer."
                        </blockquote>
                        <div>
                            <p className="font-bold text-white">Kehinde Ogunrinde</p>
                            <p className="text-sm text-gray-400">Co-founder, Ken & Kay Accounting</p>
                        </div>
                    </div>
                    <div className="bg-gray-800 p-8 rounded-lg shadow-lg flex flex-col">
                        <blockquote className="text-lg text-gray-300 italic mb-6 flex-grow">
                            "We worked closely with the RefacTech engineers to build our new Health Management System. Their expertise was crucial in developing a platform that effectively manages both our patients and medical inventories."
                        </blockquote>
                        <div>
                            <p className="font-bold text-white">Chidinma Okoro</p>
                            <p className="text-sm text-gray-400">Chief Medical Officer, Greenfield Healthcare</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* About Us Section */}
        <section id="about" className="py-20 md:py-32 bg-green-900/20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-extrabold mb-4">About RefacTech</h2>
                    <div className="mt-2 h-1 w-20 bg-green-400 mx-auto rounded mb-6"></div>
                    <p className="text-gray-300 text-lg mb-4">
                        At RefacTech, we are more than just developers; we are architects of digital transformation. Founded by a team of passionate software engineers, we saw a critical gap in the market: the need for a partner who could not only build robust software but also manage its lifecycle and scale it for future demands.
                    </p>
                    <p className="text-gray-300 text-lg">
                        Our mission is to empower businesses by transforming their technological core. Whether it's building a custom solution from the ground up, modernizing a legacy system, or managing day-to-day operations, we provide the expertise that turns technological liabilities into strategic assets.
                    </p>
                </div>
            </div>
        </section>
        
        {/* Careers Section */}
        <section id="careers" className="py-20 md:py-32">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                <h2 className="text-3xl md:text-4xl font-extrabold">Join Our Team</h2>
                <div className="mt-4 h-1 w-20 bg-green-400 mx-auto rounded"></div>
                <p className="mt-6 max-w-3xl mx-auto text-lg text-gray-300">
                    We are always on the lookout for passionate and talented individuals to join our mission. While we don't have any specific open positions at the moment, we believe in making connections with great people.
                </p>
                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto text-left">
                    <div className="bg-gray-800 p-6 rounded-lg flex items-start space-x-4">
                        <Clock className="h-8 w-8 text-green-400 flex-shrink-0 mt-1" />
                        <div>
                            <h3 className="text-lg font-bold">Flexible Work Hours</h3>
                            <p className="text-gray-400">Choose your own hours and create a work-life balance that suits you.</p>
                        </div>
                    </div>
                    <div className="bg-gray-800 p-6 rounded-lg flex items-start space-x-4">
                        <BookOpen className="h-8 w-8 text-green-400 flex-shrink-0 mt-1" />
                        <div>
                            <h3 className="text-lg font-bold">Learning & Development</h3>
                            <p className="text-gray-400">We invest in your growth with access to courses, conferences, and more.</p>
                        </div>
                    </div>
                    <div className="bg-gray-800 p-6 rounded-lg flex items-start space-x-4">
                        <Coffee className="h-8 w-8 text-green-400 flex-shrink-0 mt-1" />
                        <div>
                            <h3 className="text-lg font-bold">Paid Time Off</h3>
                            <p className="text-gray-400">We encourage you to take time off to rest, recharge, and explore.</p>
                        </div>
                    </div>
                    <div className="bg-gray-800 p-6 rounded-lg flex items-start space-x-4">
                        <Globe className="h-8 w-8 text-green-400 flex-shrink-0 mt-1" />
                        <div>
                            <h3 className="text-lg font-bold">Full Remote Work</h3>
                            <p className="text-gray-400">Work from anywhere in the world. We are a globally distributed team.</p>
                        </div>
                    </div>
                </div>
                <p className="mt-12 max-w-3xl mx-auto text-lg text-gray-300">
                    If you are an expert in system modernization, cloud architecture, or DevOps and believe you would be a great fit, we'd love to hear from you.
                </p>
                <div className="mt-8">
                    <a href="mailto:enquiry@refactech.io" className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-full text-black bg-green-400 hover:bg-green-500 md:text-lg transition-transform transform hover:scale-105 shadow-lg">
                        Send Us Your Resume <Mail className="ml-2 h-5 w-5" />
                    </a>
                </div>
            </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 md:py-32 bg-green-900/20">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-extrabold">Let's Build the Future, Together</h2>
                    <p className="mt-4 text-lg text-gray-400">
                        Ready to discuss your project? Reach out to us for a free consultation.
                    </p>
                     <div className="mt-6 space-y-2 text-lg text-green-400">
                        <div className="flex justify-center items-center">
                            <Phone className="w-5 h-5 mr-2"/>
                            <a href="tel:+2348081733641" className="hover:text-green-300">+234-808-173-3641</a>
                        </div>
                        <div className="flex justify-center items-center">
                             <Mail className="w-5 h-5 mr-2"/>
                            <a href="mailto:enquiry@refactech.io" className="hover:text-green-300">enquiry@refactech.io</a>
                        </div>
                    </div>
                    <div className="mt-4 h-1 w-20 bg-green-400 mx-auto rounded"></div>
                </div>
                <div className="mt-12 max-w-lg mx-auto">
                    <form action="#" method="POST" className="grid grid-cols-1 gap-y-6" id="contact-form">
                        <div>
                            <label htmlFor="full-name" className="sr-only">Full name</label>
                            <input type="text" name="full-name" id="full-name" autoComplete="name" className="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-green-500 focus:border-green-500 border-gray-700 rounded-md bg-gray-800 text-white" placeholder="Full name" />
                        </div>
                        <div>
                            <label htmlFor="email" className="sr-only">Email</label>
                            <input id="email" name="email" type="email" autoComplete="email" className="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-green-500 focus:border-green-500 border-gray-700 rounded-md bg-gray-800 text-white" placeholder="Email" />
                        </div>
                         <div>
                            <label htmlFor="phone" className="sr-only">Phone</label>
                            <input type="text" name="phone" id="phone" autoComplete="tel" className="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-green-500 focus:border-green-500 border-gray-700 rounded-md bg-gray-800 text-white" placeholder="Phone" />
                        </div>
                        <div>
                            <label htmlFor="message" className="sr-only">Message</label>
                            <textarea id="message" name="message" rows="4" className="block w-full shadow-sm py-3 px-4 placeholder-gray-500 focus:ring-green-500 focus:border-green-500 border-gray-700 rounded-md bg-gray-800 text-white" placeholder="Tell us about your project..."></textarea>
                        </div>
                        <div class="g-recaptcha" data-sitekey="6LeaUZ8rAAAAAIYZOThS77A0LOWWCUeUrrCa2E5i"></div>
                        <div>
                            <button type="submit" className="w-full inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-black bg-green-400 hover:bg-green-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-transform transform hover:scale-105">
                                Send Message
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-black border-t border-gray-800">
        <div className="container mx-auto py-12 px-4 sm:px-6 lg:px-8">
           <div className="md:flex md:items-center md:justify-between">
                <div className="flex justify-center space-x-6 md:order-2">
                    {/* Add social media links here if needed */}
                </div>
                <div className="mt-8 md:mt-0 md:order-1 text-center md:text-left">
                    <p className="text-base text-gray-400">&copy; {new Date().getFullYear()} RefacTech. All rights reserved.</p>
                    <address className="mt-4 text-base text-gray-400 not-italic">
                        <p>53, St. Finbarrs College Road, Lagos, Nigeria</p>
                        <p className="mt-2"><a href="tel:+2348081733641" className="hover:text-green-400 transition-colors duration-300">+234-808-173-3641</a></p>
                        <p className="mt-2"><a href="mailto:enquiry@refactech.io" className="hover:text-green-400 transition-colors duration-300">enquiry@refactech.io</a></p>
                    </address>
                </div>
           </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
